import { useCallback, useState } from 'react'

import { isEmbedded } from '@/config'
import { isLocal } from '@/utils'

export const DEFAULT_DEMO_SITE_URL = 'https://www.theevergreenessential.com/somerville/'

/**
 * Standalone app only: localhost (or 127.0.0.1) always, or any host with
 * ?showDemoSite=true. Never inside widget.js, where the host page is real.
 */
export function shouldShowDemoSite(): boolean {
  if (isEmbedded()) return false
  try {
    const params = new URLSearchParams(window.location.search)
    if (params.get('showDemoSite') === 'true') return true
    const host = window.location.hostname
    return isLocal() || host === '127.0.0.1'
  } catch {
    return false
  }
}

export function getDemoSiteUrl(): string {
  try {
    const override = new URLSearchParams(window.location.search).get('demoSiteUrl')
    if (override) return override
  } catch {
    // ignore
  }
  return DEFAULT_DEMO_SITE_URL
}

function normalizeDemoUrl(value: string): string {
  const trimmed = value.trim()
  if (!trimmed) return DEFAULT_DEMO_SITE_URL
  if (/^https?:\/\//i.test(trimmed)) return trimmed
  return `https://${trimmed}`
}

/**
 * Full-viewport host-page stand-in for embed demos. Sits behind the floating
 * widget so local / ?showDemoSite=true looks like a real merchant site.
 */
export default function DemoSiteBackdrop() {
  const [draft, setDraft] = useState(getDemoSiteUrl)
  const [src, setSrc] = useState(getDemoSiteUrl)

  const applyUrl = useCallback(() => {
    const next = normalizeDemoUrl(draft)
    setDraft(next)
    setSrc(next)
  }, [draft])

  if (!shouldShowDemoSite()) return null

  return (
    <>
      <iframe
        title='Demo merchant site'
        src={src}
        referrerPolicy='no-referrer-when-downgrade'
        style={{
          position: 'fixed',
          inset: 0,
          width: '100vw',
          height: '100dvh',
          border: 'none',
          margin: 0,
          padding: 0,
          zIndex: 0,
          backgroundColor: '#f5f5f5',
        }}
      />
      <input
        type='url'
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={applyUrl}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault()
            applyUrl()
            e.currentTarget.blur()
          }
        }}
        aria-label='Demo site URL'
        placeholder='Demo site URL'
        spellCheck={false}
        style={{
          position: 'fixed',
          top: 8,
          right: 8,
          zIndex: 2147483646,
          width: 'min(420px, calc(100vw - 16px))',
          padding: '6px 10px',
          fontSize: 12,
          fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
          color: '#111',
          backgroundColor: 'rgba(255,255,255,0.92)',
          border: '1px solid rgba(0,0,0,0.18)',
          borderRadius: 6,
          outline: 'none',
          opacity: 0.1,
          transition: 'opacity 0.15s ease',
        }}
        onFocus={(e) => {
          e.currentTarget.style.opacity = '1'
        }}
        onMouseEnter={(e) => {
          if (document.activeElement !== e.currentTarget) {
            e.currentTarget.style.opacity = '0.55'
          }
        }}
        onMouseLeave={(e) => {
          if (document.activeElement !== e.currentTarget) {
            e.currentTarget.style.opacity = '0.1'
          }
        }}
      />
    </>
  )
}
